import { Pwa } from "./Pwa";

/**
 * Push notifications from the house — standard Web Push from core-api
 * (VAPID), no OneSignal.
 *
 * A subscription belongs to this origin, i.e. to this house's subdomain: the
 * same phone on two houses has two independent subscriptions, each stored by
 * core-api with its house. This module subscribes the device (with the
 * platform's public key), posts the subscription to core-api and keeps it in
 * step with the session: posted again after signing in or out, so the stored
 * row knows whose device it is (or that it is nobody's any more).
 *
 * The notifications themselves are shown by the Service Worker (pwa/sw.js),
 * so all of this needs one — release builds only, see core/Pwa.js — and on
 * iOS the app installed to the Home Screen, the only place Safari allows push.
 *
 * state():
 *   "unsupported"  no Service Worker / Push API, no house, or push off on the server
 *   "install"      iOS outside the installed app — install first
 *   "denied"       the guest blocked notifications in the browser
 *   "off"          possible, not subscribed
 *   "on"           subscribed
 */
export class Push
{
    static DEVICE_KEY = "pushDeviceId";

    constructor( app )
    {
        this.app = app;
        this._publicKey = undefined;
    }

    static hasApi()
    {
        return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    }

    async _registration()
    {
        if ( !Push.hasApi() || Pwa.isDevBuild() )
        {
            return null;
        }
        try
        {
            return ( await navigator.serviceWorker.getRegistration() ) || null;
        }
        catch ( e )
        {
            return null;
        }
    }

    /** The platform's VAPID public key, or null when push is off on the server. */
    async _serverKey()
    {
        if ( undefined === this._publicKey )
        {
            try
            {
                const { result } = await this.app.api.get( "/push/public-key" );
                this._publicKey = ( result && result.data && result.data.enabled ) ? result.data.publicKey : null;
            }
            catch ( e )
            {
                // Unknown, not "off": ask again next time.
                return null;
            }
        }
        return this._publicKey;
    }

    async _subscription()
    {
        const registration = await this._registration();
        return registration ? registration.pushManager.getSubscription() : null;
    }

    async state()
    {
        if ( !Push.hasApi() || !this.app.accommodation?.id )
        {
            return "unsupported";
        }
        if ( Pwa.isIos() && !Pwa.isStandalone() )
        {
            return "install";
        }
        if ( !( await this._registration() ) || !( await this._serverKey() ) )
        {
            return "unsupported";
        }
        if ( Notification.permission === "denied" )
        {
            return "denied";
        }
        return ( await this._subscription() ) ? "on" : "off";
    }

    /**
     * Ask for permission and subscribe. Must run from a tap — browsers refuse
     * the permission prompt otherwise. Resolves to the new state().
     */
    async enable()
    {
        const registration = await this._registration();
        const key = await this._serverKey();
        if ( !registration || !key || !this.app.accommodation?.id )
        {
            return this.state();
        }

        const permission = await Notification.requestPermission();
        if ( permission !== "granted" )
        {
            return this.state();
        }

        let subscription = await registration.pushManager.getSubscription();
        if ( !subscription )
        {
            subscription = await registration.pushManager.subscribe( {
                userVisibleOnly : true,
                applicationServerKey : Push.base64UrlToUint8Array( key )
            } );
        }
        await this._post( subscription );
        return this.state();
    }

    /** Unsubscribe this device and drop its row. Resolves to the new state(). */
    async disable()
    {
        const subscription = await this._subscription();
        if ( subscription )
        {
            const endpoint = subscription.endpoint;
            await subscription.unsubscribe();
            try
            {
                await this.app.api.post( "/push/subscription/remove", { endpoint } );
            }
            catch ( e )
            {
                // The push service already forgot it; core-api drops the row
                // on the next send (410).
                console.error( e );
            }
        }
        return this.state();
    }

    /**
     * Re-post an existing subscription: after sign-in/sign-out (whose device),
     * a language switch, or on start (a row the server lost comes back).
     * Never asks for anything.
     */
    async sync()
    {
        try
        {
            if ( !this.app.accommodation?.id || Notification.permission !== "granted" )
            {
                return;
            }
            const subscription = await this._subscription();
            if ( subscription )
            {
                await this._post( subscription );
            }
        }
        catch ( e )
        {
            console.error( e );
        }
    }

    async _post( subscription )
    {
        await this.app.api.post( "/push/subscription", {
            accommodationId : this.app.accommodation.id,
            deviceId : this._deviceId(),
            subscription : subscription.toJSON(),
            language : this.app.l18n?.getCurrentLanguage?.() || undefined
        } );
    }

    /** This installation's id (per origin, like the subscription). */
    _deviceId()
    {
        let id = this.app.storage.load( Push.DEVICE_KEY );
        if ( !id )
        {
            id = ( crypto.randomUUID ? crypto.randomUUID() : String( Date.now() ) + Math.random().toString( 16 ).slice( 2 ) );
            this.app.storage.save( Push.DEVICE_KEY, id );
        }
        return id;
    }

    static base64UrlToUint8Array( value )
    {
        const padded = ( value + "=".repeat( ( 4 - value.length % 4 ) % 4 ) ).replace( /-/g, "+" ).replace( /_/g, "/" );
        const raw = atob( padded );
        return Uint8Array.from( raw, ( c ) => c.charCodeAt( 0 ) );
    }
}
